import { useLocation } from '@reach/router';
import { Link } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import * as React from 'react';

import type {
  INavDropdown,
  INavLink,
  INavPage,
} from '../hooks/use-sanity-menu';
import { useSanityMenu } from '../hooks/use-sanity-menu';
import { useSanitySEOSettings } from '../hooks/use-sanity-seo-settings';
import { Logo } from '../icons/logo';
import { classNames } from '../utils/classnames';
import { ContactInfo } from './contact-info';
import InnerHTML from 'dangerously-set-html-content';

function Footer(): JSX.Element {
  const { items } = useSanityMenu();
  const { siteTitle } = useSanitySEOSettings();
  return (
    <footer className="relative bg-dark" aria-labelledby="footerHeading">
      <h2 id="footerHeading" className="sr-only">
        Footer
      </h2>
      <div className="px-4 py-12 mx-auto lg:py-16 max-w-screen-2xl sm:px-6 lg:px-8 md:space-x-10">
        <div>
          <div className="justify-between w-full md:flex">
            <div className="justify-between xl:flex md:w-4/12">
              <div>
                <Link to="/" className="inline-block mb-3 md:p-2">
                  <span className="sr-only">{siteTitle}</span>
                  <Logo aria-hidden className="w-auto h-12" />
                </Link>
              </div>
              <div>
                <ul className="space-y-4 font-semibold">
                  {items.map((item) => {
                    switch (item._type) {
                      case 'navLink':
                        return <NavLink key={item.id} item={item} />;

                      case 'navPage':
                        return <NavPage key={item.id} item={item} />;

                      case 'navDropdown':
                        return <NavDropdown key={item.id} item={item} />;

                      default:
                        return null;
                    }
                  })}
                </ul>
              </div>
            </div>
            <div className="xl:w-7/12">
              <div className="mt-12 xl:mt-0">
                <ContactInfo colourScheme="dark" />
              </div>
            </div>

            <iframe
              src="https://bookeo.com/facade-escape-room/reviews?rows=1&columns=1"
              width="200"
              height="250"
              frameBorder="0"
              className=""
            ></iframe>
            <div className="w-48">
              <div id="TA_selfserveprop157" className=" TA_selfserveprop">
                <ul id="NQqB3nYsL" className="TA_links UnUiPhgb">
                  <li id="Et2MSVhRNp8W" className="VfqQMz5tRRV4">
                    <a
                      target="_blank"
                      href="https://www.tripadvisor.com/Attraction_Review-g255329-d15597960-Reviews-Facade_Escape_Room-Port_Macquarie_New_South_Wales.html"
                    >
                      <img
                        src="https://www.tripadvisor.com/img/cdsi/img2/branding/v2/Tripadvisor_lockup_horizontal_secondary_registered-11900-2.svg"
                        alt="TripAdvisor"
                      />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <script
              async
              src="https://www.jscache.com/wejs?wtype=selfserveprop&amp;uniq=157&amp;locationId=15597960&amp;lang=en_US&amp;rating=true&amp;nreviews=5&amp;writereviewlink=true&amp;popIdx=true&amp;iswide=false&amp;border=true&amp;display_version=2"
              data-loadtrk
              onLoad="this.loadtrk=true"
            ></script>
          </div>
          <div className="w-full mx-auto mt-12 border-t border-white">
            <p className="mt-6 text-center">
              <a
                href="https://pd.design"
                target="_blank"
                className="text-xs hover:text-primary text-light md:text-sm"
              >
                Designed and developed by PD
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

interface NavLinkProps {
  item: INavLink;
}

function NavLink({ item }: NavLinkProps): JSX.Element {
  return (
    <li>
      <a
        href={item.url}
        className="text-xs font-semibold md:text-base hover:text-gray-900"
      >
        {item.title}
      </a>
    </li>
  );
}

interface NavPageProps {
  item: INavPage;
}

function NavPage({ item }: NavPageProps): JSX.Element {
  const { pathname } = useLocation();
  let href;
  if (item.page._type === 'homePage') href = '/';
  if (item.page._type === 'blogPage') href = '/blog/';
  if (item.page._type === 'page') href = `/${item.page.slug.current}/`;
  return (
    <li>
      <a
        key={item.id}
        href={href}
        className={classNames(
          pathname === href && '',
          'md:text-base font-medium text-light hover:text-primary'
        )}
      >
        {item.title}
      </a>
    </li>
  );
}

interface NavDropdownProps {
  item: INavDropdown;
}

function NavDropdown({ item }: NavDropdownProps): JSX.Element | null {
  const [firstItem] = item.dropdownItems;
  switch (firstItem._type) {
    case 'navLink':
      return <NavLink key={item.id} item={firstItem} />;

    case 'navPage':
      return <NavPage key={item.id} item={firstItem} />;

    default:
      return null;
  }
}

export { Footer };
